import {ChangeEvent, ReactNode, useCallback, useMemo} from 'react'
import {FilterModel} from '../../models/FilterModel'
import PaginationHelper from '../extras/PaginationHelper'
import {useBooleanState} from '../hooks/useBooleanState'
import {useFilterState} from '../hooks/useFilterState'
import {useOnChange} from '../hooks/useOnChange'
import {useSafeStateUpdate} from '../hooks/useSafeStateUpdate'
import {TextInput} from '../inputs'
import {MetronicIconButton} from '../inputs/MetronicIconButton'
import {PopupFilterButton} from './ControlledFilterTable/PopupFilterButton/PopupFilterButton'
import {Table, TableProps} from './Table'
import {TableColumnOptions} from './TableColumn'
import {TableSelectionCountLabel} from './TableSelectionCountLabel'
import {TableFilterStateContext} from './useTableFilterState'

export type FilterToolbar = ReactNode

type FilterTableTableProps<T> = Pick<
  TableProps<T>,
  | 'selection'
  | 'data'
  | 'onSelectionChange'
  | 'rightToolbar'
  | 'idExtractor'
  | 'actions'
  | 'hiddenColumns'
  | 'onHiddenColumnsChange'
  | 'title'
  | 'expandedRows'
  | 'expansion'
  | 'onExpandChange'
  | 'isSelectDisabled'
  | 'hideSelectAll'
>

export interface FilterTableProps<T> extends FilterTableTableProps<T> {
  onFilter: (filter: FilterModel) => void
  noPagination?: boolean
  totalItems?: number
  currentPageNumber?: number
  selectionAction?: ReactNode
  leftToolbar?: FilterToolbar
  columns: TableColumnOptions<T>[]
  className?: string
  initialFilters?: FilterModel
  advancedFilters?: ReactNode
}

export const FilterTable = <T,>({
  onFilter,
  noPagination,
  totalItems,
  currentPageNumber,
  leftToolbar,
  selectionAction,
  selection,
  data,
  onSelectionChange,
  columns,
  rightToolbar,
  className,
  initialFilters,
  advancedFilters: advancedFiltersNode,
  ...tableProps
}: FilterTableProps<T>) => {
  const safeUpdate = useSafeStateUpdate()
  const {
    state: isPopupOpen,
    enableState: openPopup,
    disableState: closePopup,
  } = useBooleanState(false)

  useOnChange(data, () => {
    safeUpdate(() => onSelectionChange && onSelectionChange([]))
  })

  const filterState = useFilterState(onFilter, {initialFilters})

  const {
    sortColumn,
    setSortColumn,
    pageNumber,
    pageSize,
    setPageSize,
    setPageNumber,
    setSearch,
    search,
    clearFilters,
    hasFilters,
    advancedFilters,
  } = filterState

  const handleSearchChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setSearch(e.target.value)
    },
    [setSearch]
  )

  const searchAction = useMemo(() => {
    return (
      <div className='w-100'>
        <div className='table-left-toolbar'>
          <TextInput
            className='filter-table-search-input'
            noMargin
            placeholder='Search'
            value={search}
            onChange={handleSearchChange}
          />
          {leftToolbar}
          {hasFilters && (
            <MetronicIconButton
              iconType='Code'
              iconName='Stop'
              activeColor='danger'
              size='md'
              onClick={clearFilters}
              tooltip='Clear Filters'
            />
          )}
        </div>
      </div>
    )
  }, [search, handleSearchChange, leftToolbar, hasFilters, clearFilters])

  const bodyActions = useMemo(() => {
    return (
      <div className='mt-3'>
        <TableSelectionCountLabel count={selection?.length} />
        {selectionAction && <div className='table-left-toolbar'>{selectionAction}</div>}
      </div>
    )
  }, [selection?.length, selectionAction])

  const hasAdvancedFiltersValue = useMemo(() => {
    return Boolean(search || Object.entries(advancedFilters).length)
  }, [advancedFilters, search])

  const handleChangePageNumber = useCallback(
    (pageNumber: number) => {
      setPageNumber(pageNumber)
      onSelectionChange && onSelectionChange([])
    },
    [onSelectionChange, setPageNumber]
  )

  const filterButton = useMemo(() => {
    const hasAdvancedFilterInputs = Boolean(advancedFiltersNode)
    if (hasAdvancedFilterInputs) {
      return (
        <PopupFilterButton
          isOpen={isPopupOpen}
          onClose={closePopup}
          onOpen={openPopup}
          hasAdvancedFilters={hasAdvancedFiltersValue}
        >
          {advancedFiltersNode}
        </PopupFilterButton>
      )
    }
    return null
  }, [advancedFiltersNode, closePopup, hasAdvancedFiltersValue, isPopupOpen, openPopup])

  return (
    <TableFilterStateContext.Provider value={filterState}>
      <div className={className}>
        <Table
          {...tableProps}
          columns={columns}
          data={data}
          onSelectionChange={onSelectionChange}
          selection={selection}
          leftToolbar={searchAction}
          sortedColumn={sortColumn}
          onSort={setSortColumn}
          body={bodyActions}
          rightToolbar={
            <>
              {rightToolbar}
              {filterButton}
            </>
          }
        />
        {!noPagination && (
          <PaginationHelper
            currentPageNumber={currentPageNumber || pageNumber}
            currentPageSize={pageSize}
            onChangePageNumber={handleChangePageNumber}
            onChangePageSize={setPageSize}
            total={totalItems}
          />
        )}
      </div>
    </TableFilterStateContext.Provider>
  )
}

export const INITIAL_TABLE_PAGE_SIZE = 10
